import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ParsedData, ExampleRatings, Entity, CustomTheme, FilterMode, Example, RejectedPrediction, LabelDefinition } from './types';
import { parseNERFile, parseNERContent } from './utils/fileParser';
import { exportToJSON, calculateModelSummaries } from './utils/exportUtils';
import {
  loadState,
  saveState,
  clearState,
  loadBatchScope,
  saveBatchScope,
  BatchScope,
} from './utils/storage';
import { filterExamples } from './utils/disagreement';
import LandingPage, { ONBOARDED_KEY } from './components/LandingPage';
import { loadSampleData } from './utils/sampleData';
import ComparisonView from './components/ComparisonView';
// NewExampleCard removed in favor of inline separators
import ModelSummary from './components/ModelSummary';
import EntityReference from './components/EntityReference';
import SettingsDrawer from './components/SettingsDrawer';
import ConfirmModal from './components/ConfirmModal';

function App() {
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>('');
  const [scores, setScores] = useState<ExampleRatings>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [labelFilters, setLabelFilters] = useState<Set<string>>(new Set());
  const [newExampleText, setNewExampleText] = useState<string>('');
  const [isEditingFileName, setIsEditingFileName] = useState<boolean>(false);
  const [fileNameDraft, setFileNameDraft] = useState<string>('');
  const fileNameInputRef = useRef<HTMLInputElement>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const [allowAddExamples, setAllowAddExamples] = useState<boolean>(false);
  const [allowBatchLabelRenaming, setAllowBatchLabelRenaming] = useState<boolean>(true);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  // BQ-19: batch-ops scope toggle (sticky in localStorage; loaded on mount).
  const [batchScope, setBatchScopeState] = useState<BatchScope>(() => loadBatchScope());
  // BQ-19: filter-aware export — when true and the user clicks Export JSON
  // we open a small chooser instead of exporting everything immediately.
  const [isExportScopeOpen, setIsExportScopeOpen] = useState<boolean>(false);

  // BQ-15: undo banner for the most recent promote/reject action. Auto-dismisses
  // after 5s. The `undo` thunk reverses the last operation.
  const [undoBanner, setUndoBanner] = useState<{ message: string; undo: () => void } | null>(null);
  const undoTimerRef = useRef<number | null>(null);

  // BQ-15: same-span-different-label confirm. When promote sees an existing
  // gold annotation at the same (start, end) but with a different label, we
  // open this modal to ask whether to replace.
  const [pendingReplace, setPendingReplace] = useState<{
    exampleId: string;
    modelName: string;
    incoming: Entity;
    existingLabel: string;
  } | null>(null);

  // Load persisted state on mount
  useEffect(() => {
    const saved = loadState();
    if (saved) {
      setParsedData(saved.parsedData);
      setCurrentFileName(saved.currentFileName);
      setScores(saved.scores);
      setSearchTerm(saved.searchTerm || '');
      // Don't persist label filters to avoid confusion on reload
      // setLabelFilters(new Set(saved.labelFilters || []));
      setLabelFilters(new Set()); // Always start with no filters
      if (typeof saved.allowAddExamples === 'boolean') {
        setAllowAddExamples(saved.allowAddExamples);
      }
      if (typeof saved.allowBatchLabelRenaming === 'boolean') {
        setAllowBatchLabelRenaming(saved.allowBatchLabelRenaming);
      }
      if (
        saved.filterMode === 'all' ||
        saved.filterMode === 'disagreements' ||
        saved.filterMode === 'errors-vs-gold' ||
        saved.filterMode === 'unscored'
      ) {
        setFilterMode(saved.filterMode);
      }
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleAddExampleInternal = useCallback((text: string, insertAfterId?: string | null) => {
    if (!parsedData) return;
    const value = text.trim();
    if (!value) return;

    const existingIds = new Set(parsedData.examples.map(q => q.id));
    let counter = parsedData.examples.length + 1;
    let newId = `q${counter}`;
    while (existingIds.has(newId)) { counter += 1; newId = `q${counter}`; }

    const newExample = { id: newId, text: value, modelResponses: [], humanAnnotations: [] as Entity[] };

    setParsedData(prev => {
      if (!prev) return prev;
      if (!insertAfterId) {
        return { ...prev, examples: [newExample, ...prev.examples] };
      }
      const idx = prev.examples.findIndex(q => q.id === insertAfterId);
      if (idx === -1) return { ...prev, examples: [...prev.examples, newExample] };
      const before = prev.examples.slice(0, idx + 1);
      const after = prev.examples.slice(idx + 1);
      return { ...prev, examples: [...before, newExample, ...after] };
    });
  }, [parsedData]);

  // Listen for add-example events dispatched by separators in the ComparisonView
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      handleAddExampleInternal(detail.text, detail.insertAfterId);
    };
    window.addEventListener('add-example', handler as EventListener);
    return () => window.removeEventListener('add-example', handler as EventListener);
  }, [parsedData, handleAddExampleInternal]);

  // Autosave on state changes (but only if we have data loaded)
  useEffect(() => {
    if (parsedData) {
      saveState({
        parsedData,
        currentFileName,
        scores,
        searchTerm,
        labelFilters: Array.from(labelFilters),
        allowAddExamples,
        allowBatchLabelRenaming,
        filterMode,
      });
    }
  }, [parsedData, currentFileName, scores, searchTerm, labelFilters, allowAddExamples, allowBatchLabelRenaming, filterMode]);

  // Focus filename input when entering edit mode
  useEffect(() => {
    if (isEditingFileName) {
      const id = requestAnimationFrame(() => fileNameInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [isEditingFileName]);

  const startEditFileName = useCallback(() => {
    setFileNameDraft(currentFileName || 'Untitled.json');
    setIsEditingFileName(true);
  }, [currentFileName]);

  const commitEditFileName = useCallback(() => {
    const next = fileNameDraft.trim();
    if (next && next !== currentFileName) {
      setCurrentFileName(next);
    }
    setIsEditingFileName(false);
  }, [fileNameDraft, currentFileName]);

  const cancelEditFileName = useCallback(() => {
    setIsEditingFileName(false);
    setFileNameDraft('');
  }, []);

  // BQ-22: parseNERContent is now async (Ajv-validates JSON before parse), so
  // this handler is too. Failure cases throw a path-keyed error message that
  // we surface in the existing alert() until BQ-7 wires a proper toast.
  const handleFileUpload = useCallback(async (file: File, content: string) => {
    try {
      console.log('Uploading file:', file.name);
      console.log('Content preview:', content.substring(0, 200));

      const data = await parseNERContent(content, file.name);

      console.log('Parsed data:', data);
      console.log('Examples found:', data.examples.length);
      console.log('Model names:', data.modelNames);

      // Allow empty projects (0 examples) to load

      setParsedData(data);
      setCurrentFileName(file.name);
      setScores({});
      setSearchTerm('');
      setLabelFilters(new Set());
      setFilterMode('all');
      setAllowBatchLabelRenaming(true); // Default to enabled
      try { localStorage.setItem(ONBOARDED_KEY, 'true'); } catch { /* noop */ }
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error parsing file. Please check the format.\n\nError: ' + (error as Error).message);
    }
  }, []);

  // BQ-22: "Try sample data" CTA on the landing page. Loads
  // /example-data.json via the JSON parser and seeds the workspace so a
  // first-time visitor can explore every feature in one click.
  const handleLoadSample = useCallback(async () => {
    try {
      const data = await loadSampleData();
      setParsedData(data);
      setCurrentFileName('example-data.json');
      setScores({});
      setSearchTerm('');
      setLabelFilters(new Set());
      setFilterMode('all');
      setAllowBatchLabelRenaming(true);
      try { localStorage.setItem(ONBOARDED_KEY, 'true'); } catch { /* noop */ }
    } catch (error) {
      console.error('Error loading sample data:', error);
      alert('Could not load sample data. Please try again.\n\nError: ' + (error as Error).message);
    }
  }, []);

  const handleClear = useCallback(() => {
    setParsedData(null);
    setCurrentFileName('');
    setScores({});
    setSearchTerm('');
    setLabelFilters(new Set());
    setFilterMode('all');
    clearState();
  }, []);

  const handleScoreUpdate = useCallback((exampleId: string, modelName: string, score: any) => {
    setScores(prev => ({
      ...prev,
      [exampleId]: {
        ...prev[exampleId],
        [modelName]: score
      }
    }));
  }, []);

  const handleAnnotationsUpdate = useCallback((exampleId: string, annotations: Entity[]) => {
    setParsedData(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        examples: prev.examples.map(q =>
          q.id === exampleId
            ? { ...q, humanAnnotations: annotations }
            : q
        )
      };
    });
  }, []);

  // BQ-15: small helper — show a 5s inline undo banner. Subsequent calls
  // replace any active banner so the timer always reflects the latest action.
  const showUndoBanner = useCallback((message: string, undo: () => void) => {
    if (undoTimerRef.current) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
    setUndoBanner({ message, undo });
    undoTimerRef.current = window.setTimeout(() => {
      setUndoBanner(null);
      undoTimerRef.current = null;
    }, 5000);
  }, []);

  // Cleanup pending undo timer on unmount.
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) {
        window.clearTimeout(undoTimerRef.current);
      }
    };
  }, []);

  // BQ-15: undo handlers — pop the most recent (start, end, label) from
  // humanAnnotations / rejectedPredictions on the targeted example.
  const handleUndoPromote = useCallback((exampleId: string, entity: Entity) => {
    setParsedData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        examples: prev.examples.map(q => {
          if (q.id !== exampleId) return q;
          const ha = (q.humanAnnotations ?? []).filter(
            (e) =>
              !(
                e.start === entity.start &&
                e.end === entity.end &&
                e.label === entity.label
              )
          );
          return { ...q, humanAnnotations: ha };
        }),
      };
    });
    setUndoBanner(null);
    if (undoTimerRef.current) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  }, []);

  const handleUndoReject = useCallback((exampleId: string, modelName: string, entity: Entity) => {
    setParsedData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        examples: prev.examples.map(q => {
          if (q.id !== exampleId) return q;
          const rp = (q.rejectedPredictions ?? []).filter(
            (r) =>
              !(
                r.modelName === modelName &&
                r.entity.start === entity.start &&
                r.entity.end === entity.end &&
                r.entity.label === entity.label
              )
          );
          return { ...q, rejectedPredictions: rp };
        }),
      };
    });
    setUndoBanner(null);
    if (undoTimerRef.current) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  }, []);

  // BQ-15: promote a model prediction to humanAnnotations. Dedup against
  // existing humanAnnotations on (start, end, label). Same-span-different-label
  // collisions open a confirm modal.
  const handlePromoteEntity = useCallback((exampleId: string, modelName: string, entity: Entity) => {
    if (!parsedData) return;
    const example = parsedData.examples.find(e => e.id === exampleId);
    if (!example) return;
    const existing = example.humanAnnotations ?? [];

    const exactDup = existing.find(
      (e) =>
        e.start === entity.start &&
        e.end === entity.end &&
        e.label === entity.label
    );
    if (exactDup) {
      // Already promoted — no-op (silent, no banner).
      return;
    }

    const sameSpanDifferentLabel = existing.find(
      (e) =>
        e.start === entity.start &&
        e.end === entity.end &&
        e.label !== entity.label
    );
    if (sameSpanDifferentLabel) {
      setPendingReplace({
        exampleId,
        modelName,
        incoming: entity,
        existingLabel: sameSpanDifferentLabel.label,
      });
      return;
    }

    // Append a copy so we don't share references with modelResponses.
    const copy: Entity = { ...entity };
    setParsedData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        examples: prev.examples.map(q =>
          q.id === exampleId
            ? { ...q, humanAnnotations: [...(q.humanAnnotations ?? []), copy] }
            : q
        ),
      };
    });
    showUndoBanner('Promoted to gold ✓', () => handleUndoPromote(exampleId, copy));
  }, [parsedData, showUndoBanner, handleUndoPromote]);

  // BQ-15: reject a model prediction (skip if already rejected for this
  // (modelName, start, end, label)).
  const handleRejectEntity = useCallback((exampleId: string, modelName: string, entity: Entity) => {
    if (!parsedData) return;
    const example = parsedData.examples.find(e => e.id === exampleId);
    if (!example) return;
    const existing = example.rejectedPredictions ?? [];
    const dup = existing.find(
      (r) =>
        r.modelName === modelName &&
        r.entity.start === entity.start &&
        r.entity.end === entity.end &&
        r.entity.label === entity.label
    );
    if (dup) return;

    const rec: RejectedPrediction = { modelName, entity: { ...entity } };
    setParsedData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        examples: prev.examples.map(q =>
          q.id === exampleId
            ? { ...q, rejectedPredictions: [...(q.rejectedPredictions ?? []), rec] }
            : q
        ),
      };
    });
    showUndoBanner('Rejected prediction ✗', () => handleUndoReject(exampleId, modelName, rec.entity));
  }, [parsedData, showUndoBanner, handleUndoReject]);

  // BQ-15: confirm modal handlers for same-span-different-label replace.
  const confirmReplace = useCallback(() => {
    if (!pendingReplace) return;
    const { exampleId, incoming } = pendingReplace;
    const copy: Entity = { ...incoming };
    setParsedData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        examples: prev.examples.map(q => {
          if (q.id !== exampleId) return q;
          // Replace the entry that shares (start, end), keep all others.
          const next = (q.humanAnnotations ?? []).map((e) =>
            e.start === incoming.start && e.end === incoming.end ? copy : e
          );
          return { ...q, humanAnnotations: next };
        }),
      };
    });
    setPendingReplace(null);
    showUndoBanner('Promoted to gold ✓', () => {
      // Undo the replace by restoring the previous label at the same span.
      // Best-effort: drop the promoted entry; we don't recover the original
      // label here since the prior state is captured in setParsedData history
      // by React. For correctness we re-fetch the example and rewrite.
      setParsedData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          examples: prev.examples.map(q => {
            if (q.id !== exampleId) return q;
            const next = (q.humanAnnotations ?? []).filter(
              (e) =>
                !(e.start === copy.start && e.end === copy.end && e.label === copy.label)
            );
            return { ...q, humanAnnotations: next };
          }),
        };
      });
    });
  }, [pendingReplace, showUndoBanner]);

  const cancelReplace = useCallback(() => {
    setPendingReplace(null);
  }, []);

  const handleBatchLabelEdit = useCallback((
    oldLabel: string,
    newLabel: string,
    newColor: string,
    scopedExampleIds?: Set<string>
  ) => {
    if (!parsedData || !newLabel.trim()) return;

    setParsedData(prev => {
      if (!prev) return prev;

      const updatedCustomColors = { ...(prev.customLabelColors || {}) };

      // If label name changed, remove old color mapping
      // BQ-19: only drop the color mapping when applying to all examples; a
      // filtered rename intentionally keeps both label names alive across the
      // dataset so we leave the original color intact.
      const renamingAll = !scopedExampleIds;
      if (renamingAll && oldLabel !== newLabel.trim() && updatedCustomColors[oldLabel]) {
        delete updatedCustomColors[oldLabel];
      }

      // Set new color for the label
      updatedCustomColors[newLabel.trim()] = newColor;

      const inScope = (id: string) => !scopedExampleIds || scopedExampleIds.has(id);

      return {
        ...prev,
        customLabelColors: updatedCustomColors,
        examples: prev.examples.map(example => {
          if (!inScope(example.id)) return example;
          return {
            ...example,
            // Update entities in model responses
            modelResponses: example.modelResponses.map(response => ({
              ...response,
              entities: response.entities.map(entity =>
                entity.label === oldLabel
                  ? { ...entity, label: newLabel.trim() }
                  : entity
              )
            })),
            // Update human annotations
            humanAnnotations: example.humanAnnotations?.map(entity =>
              entity.label === oldLabel
                ? { ...entity, label: newLabel.trim() }
                : entity
            )
          };
        })
      };
    });
  }, [parsedData]);

  const handleSaveTheme = useCallback((theme: Omit<CustomTheme, 'id'>) => {
    setParsedData(prev => {
      if (!prev) return prev;

      const savedThemes = prev.savedThemes || [];

      // Check if theme already exists (same colors)
      const existingTheme = savedThemes.find(
        t => t.bgColor === theme.bgColor &&
             t.textColor === theme.textColor &&
             t.borderColor === theme.borderColor
      );

      if (existingTheme) {
        // Theme already exists, don't add duplicate
        return prev;
      }

      // Create new theme with unique ID
      const newTheme: CustomTheme = {
        ...theme,
        id: `theme-${Date.now()}-${Math.random().toString(36).substring(7)}`
      };

      return {
        ...prev,
        savedThemes: [...savedThemes, newTheme]
      };
    });
  }, []);

  const handleDeleteLabel = useCallback((
    label: string,
    alsoDeleteLabelDefinition: boolean,
    scopedExampleIds?: Set<string>
  ) => {
    setParsedData(prev => {
      if (!prev) return prev;

      const updatedCustomColors = { ...(prev.customLabelColors || {}) };

      // BQ-19: only remove the label *definition* when scope is "all" — a
      // filtered delete shouldn't make the label disappear from the available
      // labels list since it may still exist in unfiltered examples.
      const deletingAll = !scopedExampleIds;
      if (deletingAll && alsoDeleteLabelDefinition) {
        delete updatedCustomColors[label];
      }

      const inScope = (id: string) => !scopedExampleIds || scopedExampleIds.has(id);

      return {
        ...prev,
        customLabelColors: updatedCustomColors,
        examples: prev.examples.map(example => {
          if (!inScope(example.id)) return example;
          return {
            ...example,
            // Remove entities from model responses
            modelResponses: example.modelResponses.map(response => ({
              ...response,
              entities: response.entities.filter(entity => entity.label !== label)
            })),
            // Remove from human annotations
            humanAnnotations: example.humanAnnotations?.filter(entity => entity.label !== label)
          };
        })
      };
    });
  }, []);

  const handleMergeLabel = useCallback((
    fromLabel: string,
    toLabel: string,
    scopedExampleIds?: Set<string>
  ) => {
    setParsedData(prev => {
      if (!prev) return prev;

      const updatedCustomColors = { ...(prev.customLabelColors || {}) };

      // BQ-19: only drop the source-label color when merging across all
      // examples — partial-scope merges leave the source alive elsewhere.
      const mergingAll = !scopedExampleIds;
      if (mergingAll && updatedCustomColors[fromLabel]) {
        delete updatedCustomColors[fromLabel];
      }

      const inScope = (id: string) => !scopedExampleIds || scopedExampleIds.has(id);

      return {
        ...prev,
        customLabelColors: updatedCustomColors,
        examples: prev.examples.map(example => {
          if (!inScope(example.id)) return example;
          return {
            ...example,
            // Update entities in model responses
            modelResponses: example.modelResponses.map(response => ({
              ...response,
              entities: response.entities.map(entity =>
                entity.label === fromLabel
                  ? { ...entity, label: toLabel }
                  : entity
              )
            })),
            // Update human annotations
            humanAnnotations: example.humanAnnotations?.map(entity =>
              entity.label === fromLabel
                ? { ...entity, label: toLabel }
                : entity
            )
          };
        })
      };
    });
  }, []);

  const handleCreateLabel = useCallback((label: string, color: string) => {
    setParsedData(prev => {
      if (!prev) return prev;

      const updatedCustomColors = { ...(prev.customLabelColors || {}) };
      updatedCustomColors[label] = color;

      return {
        ...prev,
        customLabelColors: updatedCustomColors
      };
    });
  }, []);

  // BQ-18: persist a per-label annotation guideline. When all fields of `def`
  // are empty/undefined we delete the entry rather than writing an empty
  // shell — keeps export payloads clean for labels that never received guidance.
  const handleSaveLabelDefinition = useCallback(
    (label: string, def: LabelDefinition) => {
      setParsedData((prev) => {
        if (!prev) return prev;
        const next = { ...(prev.labelDefinitions || {}) };
        const isEmpty =
          !def.description?.trim() &&
          (def.examples?.length ?? 0) === 0 &&
          (def.counterExamples?.length ?? 0) === 0;
        if (isEmpty) {
          delete next[label];
        } else {
          next[label] = def;
        }
        return { ...prev, labelDefinitions: next };
      });
    },
    []
  );

  // BQ-19: factored out so the export-scope chooser can call it with either
  // the filtered subset or the full dataset.
  const performExportJSON = useCallback((examplesOverride?: Example[]) => {
    if (!parsedData) return;

    const jsonData = exportToJSON(parsedData, scores, examplesOverride);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ensuredName = currentFileName && currentFileName.toLowerCase().endsWith('.json')
      ? currentFileName
      : `${currentFileName.replace(/\.[^/.]+$/, '')}.json`;
    a.download = ensuredName || 'export.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [parsedData, scores, currentFileName]);



  const handleLabelFilter = useCallback((label: string, modelName: string | null) => {
    const filterKey = modelName ? `${modelName}:${label}` : label;

    setLabelFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(filterKey)) {
        newFilters.delete(filterKey);
      } else {
        newFilters.add(filterKey);
      }
      return newFilters;
    });
  }, []);

  // Apply BQ-16 disagreement filter first, then AND search + label filters.
  const modeFilteredExamples = parsedData
    ? filterExamples(parsedData.examples, filterMode, scores)
    : [];

  const filteredExamples = modeFilteredExamples.filter(example => {
    // Text search filter
    const matchesSearch = example.text.toLowerCase().includes(searchTerm.toLowerCase());

    // Label filter
    if (labelFilters.size === 0) {
      return matchesSearch;
    }

    // Check if example has any model response or human annotation that matches any active filter
    const matchesLabelFilter = Array.from(labelFilters).some(filterKey => {
      const [modelName, label] = filterKey.includes(':')
        ? filterKey.split(':')
        : [null, filterKey];

      // Check model responses
      const matchesModelResponse = example.modelResponses.some(response => {
        // If filter specifies a model, check that model
        if (modelName && response.modelName !== modelName) {
          return false;
        }

        // Check if this response has the label
        return response.entities.some(entity => entity.label === label);
      });

      // Also check human annotations if no model filter specified
      const matchesHumanAnnotation = !modelName && example.humanAnnotations?.some(entity =>
        entity.label === label
      );

      return matchesModelResponse || matchesHumanAnnotation;
    });

    return matchesSearch && matchesLabelFilter;
  });

  const modelSummaries = parsedData ? calculateModelSummaries(parsedData, scores) : [];

  // BQ-19: filter-active = any one of the three filter sources is engaged.
  // This drives both the export-scope chooser and the LabelEditor toggle.
  const isFilterActive =
    searchTerm.length > 0 || labelFilters.size > 0 || filterMode !== 'all';

  // Persist batch scope to the dedicated localStorage key whenever it
  // changes (separate from the main payload — see storage.ts for rationale).
  const setBatchScope = useCallback((next: BatchScope) => {
    setBatchScopeState(next);
    saveBatchScope(next);
  }, []);

  // BQ-19: precompute the scoped-example-id set so batch handlers can be
  // invoked from any descendant without re-running the filter pipeline.
  const filteredExampleIds = new Set(filteredExamples.map((e) => e.id));
  const totalExampleCount = parsedData?.examples.length ?? 0;
  const filteredExampleCount = filteredExamples.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div>
                <a
                  href="https://softwaresushi.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity block"
                >
                  <img
                    src="/logo.png"
                    alt="NER Tool logo"
                    className="h-8 w-50 object-contain cursor-pointer"
                  />
                </a>
                <h1 className="text-2xl font-bold text-gray-900">NER Tool</h1>
                {currentFileName && (
                  <div className="text-sm text-gray-600 mt-1 flex items-center space-x-1">
                    <span className="text-gray-500">File:</span>
                    {!isEditingFileName ? (
                      <button
                        onClick={startEditFileName}
                        className="underline decoration-dotted hover:decoration-solid hover:text-gray-900"
                        title="Click to rename"
                      >
                        {currentFileName}
                      </button>
                    ) : (
                      <input
                        ref={fileNameInputRef}
                        type="text"
                        value={fileNameDraft}
                        onChange={(e) => setFileNameDraft(e.target.value)}
                        onBlur={commitEditFileName}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitEditFileName(); }
                          else if (e.key === 'Escape') { e.preventDefault(); cancelEditFileName(); }
                        }}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-4 items-center">
              {parsedData && (
                <>
                  <button
                    onClick={() => {
                      // BQ-19: when any filter is active, ask the user which
                      // scope to export; otherwise short-circuit to "all".
                      if (isFilterActive) {
                        setIsExportScopeOpen(true);
                      } else {
                        performExportJSON();
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Export JSON
                  </button>
                  <button
                    onClick={handleClear}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Clear
                  </button>
                </>
              )}
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                title="Settings"
                aria-label="Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!parsedData ? (
          <LandingPage
            onUploadFile={handleFileUpload}
            onLoadSample={handleLoadSample}
            onStartNew={() => {
              // Initialize empty project
              setParsedData({ examples: [], modelNames: [] });
              setCurrentFileName('Untitled.json');
              setAllowAddExamples(true); // default enabled for new project
              setAllowBatchLabelRenaming(true); // default enabled for new project
              try { localStorage.setItem(ONBOARDED_KEY, 'true'); } catch { /* noop */ }
            }}
          />
        ) : (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search examples..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  {labelFilters.size > 0 && (
                    <button
                      onClick={() => setLabelFilters(new Set())}
                      className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                    >
                      Clear Filters ({labelFilters.size})
                    </button>
                  )}
                  <div className="text-sm text-gray-600">
                    {filteredExamples.length} of {parsedData.examples.length} examples
                  </div>
                </div>
              </div>

              {/* BQ-16: filter-mode segmented control. Sits below the search
                  row so it composes (AND) with both search and label
                  filters. */}
              <div className="mt-3 flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Filter mode">
                <span className="text-xs font-medium text-gray-500 mr-1">Filter:</span>
                {([
                  { value: 'all', label: 'All' },
                  { value: 'disagreements', label: 'Disagreements' },
                  { value: 'errors-vs-gold', label: 'Errors vs Gold' },
                  { value: 'unscored', label: 'Unscored' },
                ] as { value: FilterMode; label: string }[]).map(({ value, label }) => {
                  const isActive = filterMode === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => setFilterMode(value)}
                      className={
                        'px-3 py-1 text-xs font-medium rounded-md border transition-colors ' +
                        (isActive
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {/* Inline add example input removed; separators handle creation */}
            </div>

            {/* Debug Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <strong>Debug Info:</strong> {parsedData.examples.length} examples loaded, {parsedData.modelNames.length} models ({parsedData.modelNames.join(', ')})
            </div>

            {/* Entity Reference */}
            <EntityReference
              data={parsedData}
              onLabelFilter={handleLabelFilter}
              activeFilters={labelFilters}
              allowBatchLabelRenaming={allowBatchLabelRenaming}
              onBatchLabelEdit={handleBatchLabelEdit}
              onSaveTheme={handleSaveTheme}
              onDeleteLabel={handleDeleteLabel}
              onMergeLabel={handleMergeLabel}
              onCreateLabel={handleCreateLabel}
              isFilterActive={isFilterActive}
              batchScope={batchScope}
              onBatchScopeChange={setBatchScope}
              filteredExampleIds={filteredExampleIds}
              filteredExampleCount={filteredExampleCount}
              totalExampleCount={totalExampleCount}
              labelDefinitions={parsedData.labelDefinitions}
              onSaveLabelDefinition={handleSaveLabelDefinition}
            />

            {/* Model Summary */}
            {modelSummaries.length > 0 && (
              <ModelSummary summaries={modelSummaries} />
            )}

            {/* Main Content */}
            <ComparisonView
              examples={filteredExamples}
              modelNames={parsedData.modelNames}
              scores={scores}
              onScoreUpdate={handleScoreUpdate}
              onAnnotationsUpdate={handleAnnotationsUpdate}
              allowAddExamples={allowAddExamples}
              customLabelColors={parsedData.customLabelColors}
              filterMode={filterMode}
              onPromoteEntity={handlePromoteEntity}
              onRejectEntity={handleRejectEntity}
              labelDefinitions={parsedData.labelDefinitions}
              onMergeLabels={(from, to) => handleMergeLabel(from, to)}
            />

          </div>
        )}
      </div>

      <SettingsDrawer
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        allowAddExamples={allowAddExamples}
        onToggleAllowAddExamples={(next) => setAllowAddExamples(next)}
        allowBatchLabelRenaming={allowBatchLabelRenaming}
        onToggleAllowBatchLabelRenaming={(next) => setAllowBatchLabelRenaming(next)}
        batchScope={batchScope}
        isFilterActive={isFilterActive}
      />

      {/* BQ-15: undo banner. Top-right, auto-dismisses after 5s. */}
      {undoBanner && (
        <div
          className="fixed top-4 right-4 bg-gray-900 text-white text-sm rounded-md shadow-lg px-4 py-2 flex items-center gap-3"
          style={{ zIndex: 10002 }}
          role="status"
          aria-live="polite"
        >
          <span>{undoBanner.message}</span>
          <button
            type="button"
            onClick={() => undoBanner.undo()}
            className="text-blue-300 hover:text-blue-100 underline text-xs font-medium"
          >
            Undo
          </button>
        </div>
      )}

      {/* BQ-15: same-span-different-label confirm modal. Promote opens this
          when the incoming entity collides with an existing humanAnnotation
          on (start, end) but has a different label. */}
      <ConfirmModal
        isOpen={!!pendingReplace}
        title="Replace existing label?"
        message={
          pendingReplace
            ? `Replace existing label "${pendingReplace.existingLabel}" with "${pendingReplace.incoming.label}" at this span?`
            : ''
        }
        confirmLabel="Replace"
        cancelLabel="Cancel"
        onConfirm={confirmReplace}
        onCancel={cancelReplace}
      />

      {/* BQ-19: filter-aware export scope chooser. Three outcomes —
          export filtered / export all / dismiss — so a 2-button ConfirmModal
          isn't a clean fit. We render a small custom dialog instead, but
          keep the same Tailwind shell so it visually matches ConfirmModal. */}
      {isExportScopeOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
          style={{ zIndex: 10001 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-scope-title"
          onClick={() => setIsExportScopeOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsExportScopeOpen(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-2xl border border-gray-200 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 id="export-scope-title" className="text-base font-semibold text-gray-900">
                Export JSON
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-gray-700 space-y-2">
              <p>A filter is currently active. Choose what to export:</p>
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 rounded-b-lg">
              <button
                type="button"
                onClick={() => setIsExportScopeOpen(false)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsExportScopeOpen(false);
                  performExportJSON();
                }}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Export all ({totalExampleCount})
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => {
                  setIsExportScopeOpen(false);
                  performExportJSON(filteredExamples);
                }}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Export filtered ({filteredExampleCount} of {totalExampleCount})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
